// Conversation Markdown produces escaped HTML; Angular sanitizes the bound HTML again.
const escapeHtml = (text: string): string => text.replace(/[&<>"']/g, char => ({'&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;'}[char]!));
const inlineTokens = /\\[\\`*_[\]{}()#+.!>~-]|(`+)[\s\S]*?\1|\[([^\]\n]+)\]\(([^()\s]+)\)|\*\*(.+?)\*\*|__(.+?)__|~~(.+?)~~|(?<!\w)\*([^*\n]+)\*|(?<!\w)_([^_\n]+)_(?!\w)/g;

function inline(text: string, depth = 0): string {
  if (depth > 8) return escapeHtml(text);
  let html = '', cursor = 0;
  for (const match of text.matchAll(inlineTokens)) {
    const token = match[0], index = match.index!;
    html += escapeHtml(text.slice(cursor, index));
    if (token.startsWith('\\')) html += escapeHtml(token.slice(1));
    else if (match[1]) html += '<code>' + escapeHtml(token.slice(match[1].length, -match[1].length)) + '</code>';
    else if (match[2] !== undefined) {
      const url = match[3];
      const safe = /^(?:https?:\/\/|mailto:|\/(?!\/)|#)/i.test(url) && !/[\u0000-\u0020\u007f\\]/.test(url);
      html += safe ? '<a href="' + escapeHtml(url) + '" rel="noopener noreferrer">' + inline(match[2], depth + 1) + '</a>' : inline(match[2], depth + 1);
    } else {
      const pair = token.startsWith('**') || token.startsWith('__') || token.startsWith('~~');
      const tag = token.startsWith('~~') ? 'del' : pair ? 'strong' : 'em';
      html += '<' + tag + '>' + inline(token.slice(pair ? 2 : 1, pair ? -2 : -1), depth + 1) + '</' + tag + '>';
    }
    cursor = index + token.length;
  }
  return (html + escapeHtml(text.slice(cursor))).replace(/\n/g, '<br>');
}

const listLine = /^( {0,3})([-+*]|\d+[.)])\s+(.*)$/;
const fenceLine = /^ {0,3}(`{3,}|~{3,})(.*)$/;
const headingLine = /^ {0,3}(#{1,6})\s+(.+?)\s*#*\s*$/;
const ruleLine = /^ {0,3}(?:(?:-\s*){3,}|(?:\*\s*){3,}|(?:_\s*){3,})$/;
const startsBlock = (line: string): boolean => !line.trim() || fenceLine.test(line) || headingLine.test(line) || ruleLine.test(line) || listLine.test(line) || /^ {0,3}>/.test(line);

function blocks(lines: string[], depth = 0): string {
  if (depth > 12) return '<p>' + inline(lines.join('\n')) + '</p>';
  const output: string[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) { index++; continue; }
    const fence = fenceLine.exec(line);
    if (fence) {
      const code: string[] = [];
      const closing = new RegExp('^ {0,3}' + (fence[1][0] === '`' ? '`' : '~') + '{' + fence[1].length + ',}\\s*$');
      index++;
      while (index < lines.length && !closing.test(lines[index])) code.push(lines[index++]);
      if (index < lines.length) index++;
      output.push('<pre><code>' + escapeHtml(code.join('\n')) + '</code></pre>');
      continue;
    }
    const heading = headingLine.exec(line);
    if (heading) { output.push('<h' + heading[1].length + '>' + inline(heading[2]) + '</h' + heading[1].length + '>'); index++; continue; }
    if (ruleLine.test(line)) { output.push('<hr>'); index++; continue; }
    if (/^ {0,3}>/.test(line)) {
      const quote: string[] = [];
      while (index < lines.length && /^ {0,3}>/.test(lines[index])) quote.push(lines[index++].replace(/^ {0,3}> ?/, ''));
      output.push('<blockquote>' + blocks(quote, depth + 1) + '</blockquote>');
      continue;
    }
    const first = listLine.exec(line);
    if (first) {
      const indent = first[1].length, ordered = /^\d/.test(first[2]), entries: string[] = [];
      while (index < lines.length) {
        const entry = listLine.exec(lines[index]);
        if (!entry || entry[1].length !== indent || /^\d/.test(entry[2]) !== ordered || ruleLine.test(lines[index])) break;
        const contentIndent = lines[index].length - entry[3].length, content = [entry[3]];
        index++;
        while (index < lines.length) {
          const next = lines[index];
          if (!next.trim()) {
            let following = index + 1;
            while (following < lines.length && !lines[following].trim()) following++;
            if (following >= lines.length || !nextIsListContent(lines[following], indent, ordered)) break;
            content.push(''); index++; continue;
          }
          const spaces = /^ */.exec(next)![0].length;
          if (spaces <= indent) break;
          content.push(next.slice(Math.min(spaces, contentIndent))); index++;
        }
        entries.push('<li>' + blocks(content, depth + 1) + '</li>');
        while (index < lines.length && !lines[index].trim()) index++;
      }
      const tag = ordered ? 'ol' : 'ul';
      const start = ordered && parseInt(first[2], 10) !== 1 ? ' start="' + parseInt(first[2], 10) + '"' : '';
      output.push('<' + tag + start + '>' + entries.join('') + '</' + tag + '>');
      continue;
    }
    const paragraph = [line]; index++;
    while (index < lines.length && !startsBlock(lines[index])) paragraph.push(lines[index++]);
    output.push('<p>' + inline(paragraph.join('\n')) + '</p>');
  }
  return output.join('');
}

function nextIsListContent(line: string, indent: number, ordered: boolean): boolean {
  const entry = listLine.exec(line);
  return /^ */.exec(line)![0].length > indent || !!entry && entry[1].length === indent && /^\d/.test(entry[2]) === ordered;
}

export function renderAgentMarkdown(value: unknown): string {
  const text = String(value ?? '').replace(/\r\n?/g, '\n').replace(/\t/g, '    ');
  return blocks(text.split('\n'));
}
